import React from 'react';
import { Subject } from 'rxjs';
import {
    getAnswerPageConfig,
    setAnswerPageConfig,
} from '/@contexts/answer-context';
import {
    SearchExecutionState,
    setVisualizationLoading,
} from '/@services/answer/answer.util';
import { LoadingState } from '/@services/generated/graphql-types';
import { useApolloClient } from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { CancelType, TriggerAction } from './query-cancellation-metrics.util';
import { sendDataCancelBeacon } from './search-cancel.util';
import { useDataCancelMutation } from './search-service';

interface CancelSearchExecutionOptions {
    forceCancel?: boolean;
    showCancelledState?: boolean;
    cancelType?: CancelType;
    triggerAction?: TriggerAction;
}

export type TrackQueryCancelled = (
    cancelIds: string[],
    cancelType: CancelType,
    triggerAction: TriggerAction,
) => void;

type AnswerCancellationState = {
    abortSubject: Subject<void>;
    cancelIds: Set<string>;
    wasCancelled: boolean;
};

const answerCancellationRegistry = new Map<string, AnswerCancellationState>();

const getExistingAnswerCancellationState = (
    answerSessionId?: string,
): AnswerCancellationState | null => {
    if (!answerSessionId) {
        return null;
    }

    return answerCancellationRegistry.get(answerSessionId) ?? null;
};

const getOrCreateAnswerCancellationState = (
    answerSessionId?: string,
): AnswerCancellationState | null => {
    if (!answerSessionId) {
        return null;
    }

    if (!answerCancellationRegistry.has(answerSessionId)) {
        answerCancellationRegistry.set(answerSessionId, {
            abortSubject: new Subject<void>(),
            cancelIds: new Set<string>(),
            wasCancelled: false,
        });
    }

    return answerCancellationRegistry.get(answerSessionId) ?? null;
};

export const registerCancelId = (
    answerSessionId?: string,
    cancelId?: string,
): void => {
    if (!answerSessionId || !cancelId) {
        return;
    }

    getOrCreateAnswerCancellationState(answerSessionId)?.cancelIds.add(
        cancelId,
    );
};

export const unregisterCancelId = (
    answerSessionId?: string,
    cancelId?: string,
): void => {
    if (!answerSessionId || !cancelId) {
        return;
    }

    getExistingAnswerCancellationState(answerSessionId)?.cancelIds.delete(
        cancelId,
    );
};

export const getRegisteredCancelIds = (answerSessionId?: string): string[] =>
    Array.from(
        getExistingAnswerCancellationState(answerSessionId)?.cancelIds ?? [],
    );

// Clears ONLY the cancelIds Set for a session. Unlike clearAnswerCancellationState,
// it does NOT complete the abortSubject or delete the registry entry, so the
// per-session subject and wasCancelled flag survive across executions. Used to
// scope the set to the current execution so data_cancel never fires stale ids.
export const clearRegisteredCancelIds = (answerSessionId?: string): void => {
    if (!answerSessionId) {
        return;
    }
    getExistingAnswerCancellationState(answerSessionId)?.cancelIds.clear();
};

export const wasSessionCancelled = (answerSessionId?: string): boolean =>
    getExistingAnswerCancellationState(answerSessionId)?.wasCancelled ?? false;

// Clears the wasCancelled flag so post-cancel errors aren't suppressed as
// cancellation side-effects. searchExecutionState is owned by answer-context.
export const resetSessionCancelled = (answerSessionId?: string): void => {
    const state = getExistingAnswerCancellationState(answerSessionId);
    if (state) {
        state.wasCancelled = false;
    }
};

// Returns a callback that clears the cancelled state (dismiss stopped UI +
// reset wasCancelled) for a fresh action.
export const useResetCancelledState = (
    answerSessionId?: string,
): (() => void) => {
    const { searchExecutionState } = getAnswerPageConfig();
    const { setSearchExecutionState } = setAnswerPageConfig();
    return React.useCallback(() => {
        if (searchExecutionState === SearchExecutionState.Cancelled) {
            setSearchExecutionState(SearchExecutionState.Idle);
            resetSessionCancelled(answerSessionId);
        }
    }, [answerSessionId, searchExecutionState, setSearchExecutionState]);
};

export const getAnswerDataQueryAbortSubject = (
    answerSessionId?: string,
): Subject<void> => {
    const answerCancellationState = getOrCreateAnswerCancellationState(
        answerSessionId,
    );

    if (answerCancellationState) {
        return answerCancellationState.abortSubject;
    }

    return new Subject<void>();
};

export const clearAnswerCancellationState = (
    answerSessionId?: string,
): void => {
    if (!answerSessionId) {
        return;
    }

    const answerCancellationState = answerCancellationRegistry.get(
        answerSessionId,
    );
    answerCancellationState?.abortSubject.complete();
    answerCancellationRegistry.delete(answerSessionId);
};

// Synchronous read — safe to call inside event handlers without React state.
export const getActiveCancelIdCount = (answerSessionId?: string): number =>
    getExistingAnswerCancellationState(answerSessionId)?.cancelIds.size ?? 0;

export const useAnswerCancellationManager = (
    answerSessionId: string,
    cancelSearchRunPhase?: () => void,
    trackQueryCancelled?: TrackQueryCancelled,
) => {
    const client = useApolloClient();
    const { dataCancel } = useDataCancelMutation();
    const { isAnswerLoading, searchExecutionState } = getAnswerPageConfig();
    const {
        setIsAnswerLoading,
        setSearchExecutionState,
    } = setAnswerPageConfig();
    const isSearchExecutionInProgress =
        searchExecutionState === SearchExecutionState.InProgress;

    const isSearchExecutionInProgressRef = React.useRef(
        isSearchExecutionInProgress,
    );
    isSearchExecutionInProgressRef.current = isSearchExecutionInProgress;
    const dataCancelRef = React.useRef(dataCancel);
    dataCancelRef.current = dataCancel;
    const trackQueryCancelledRef = React.useRef(trackQueryCancelled);
    trackQueryCancelledRef.current = trackQueryCancelled;
    const emittedCancelledIdsRef = React.useRef<Set<string>>(new Set());

    const emitQueryCancelled = React.useCallback(
        (
            cancelIds: string[],
            cancelType: CancelType,
            triggerAction: TriggerAction,
        ) => {
            const track = trackQueryCancelledRef.current;
            if (!track) return;
            const novel = cancelIds.filter(
                id => !emittedCancelledIdsRef.current.has(id),
            );
            if (!novel.length) return;
            novel.forEach(id => emittedCancelledIdsRef.current.add(id));
            track(novel, cancelType, triggerAction);
        },
        [],
    );

    // Single effect so abort→cancel→clear order is explicit, not cross-effect LIFO.
    React.useEffect(() => {
        if (!answerSessionId) {
            return undefined;
        }
        return () => {
            if (isSearchExecutionInProgressRef.current) {
                const cancelIds = getRegisteredCancelIds(answerSessionId);
                if (cancelIds.length) {
                    emitQueryCancelled(
                        cancelIds,
                        CancelType.IMPLICIT,
                        TriggerAction.NAVIGATION,
                    );
                    getAnswerDataQueryAbortSubject(answerSessionId).next();
                    dataCancelRef.current(cancelIds).catch(() => {
                        // Best-effort backend cancel during navigation.
                    });
                }
            }
            clearAnswerCancellationState(answerSessionId);
        };
    }, [answerSessionId]);

    // sendBeacon survives pagehide. Fires on bfcache freeze too: apollo-client
    // aborts every fetch on pagehide and never re-inits (no pageshow), so a
    // restored page can't resume the query anyway — freeing the backend is free.
    React.useEffect(() => {
        if (!answerSessionId) {
            return undefined;
        }
        const handlePageHide = () => {
            if (!isSearchExecutionInProgressRef.current) {
                return;
            }
            const cancelIds = getRegisteredCancelIds(answerSessionId);
            if (cancelIds.length) {
                emitQueryCancelled(
                    cancelIds,
                    CancelType.IMPLICIT,
                    TriggerAction.PAGE_HIDDEN,
                );
                sendDataCancelBeacon(cancelIds);
            }
        };
        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [answerSessionId]);

    const hasSeenAnswerLoadingForExecutionRef = React.useRef(false);
    React.useEffect(() => {
        if (!isSearchExecutionInProgress) {
            hasSeenAnswerLoadingForExecutionRef.current = false;
            return;
        }

        if (isAnswerLoading) {
            hasSeenAnswerLoadingForExecutionRef.current = true;
            return;
        }

        if (hasSeenAnswerLoadingForExecutionRef.current && !isAnswerLoading) {
            // Execution completed: drop its cancelIds so a later cancel never
            // fires the just-finished query's (now stale) trace id.
            clearRegisteredCancelIds(answerSessionId);
            setSearchExecutionState(SearchExecutionState.Idle);
            hasSeenAnswerLoadingForExecutionRef.current = false;
        }
    }, [
        answerSessionId,
        isAnswerLoading,
        isSearchExecutionInProgress,
        setSearchExecutionState,
    ]);

    // Dismiss the stopped UI when a new action loads after a cancel. Chart-type
    // changes (via setChartType), downloads, and editors all flip
    // isAnswerLoading — one signal covers them all. Display-mode toggle and
    // source switch reset explicitly in their handlers (no isAnswerLoading).
    React.useEffect(() => {
        if (
            searchExecutionState === SearchExecutionState.Cancelled &&
            isAnswerLoading
        ) {
            resetSessionCancelled(answerSessionId);
            setSearchExecutionState(SearchExecutionState.Idle);
        }
    }, [
        answerSessionId,
        isAnswerLoading,
        searchExecutionState,
        setSearchExecutionState,
    ]);

    const cancelSearchExecution = React.useCallback(
        ({
            forceCancel = false,
            showCancelledState = true,
            cancelType = CancelType.IMPLICIT,
            triggerAction = TriggerAction.TOKEN_EDITED,
        }: CancelSearchExecutionOptions = {}): boolean => {
            if (!forceCancel && !isSearchExecutionInProgress) {
                return false;
            }

            const cancelIds = getRegisteredCancelIds(answerSessionId);
            emitQueryCancelled(cancelIds, cancelType, triggerAction);
            const state = getOrCreateAnswerCancellationState(answerSessionId);
            if (state) {
                state.wasCancelled = true;
            }
            cancelSearchRunPhase?.();
            setSearchExecutionState(
                showCancelledState
                    ? SearchExecutionState.Cancelled
                    : SearchExecutionState.Idle,
            );
            setIsAnswerLoading(false);

            try {
                setVisualizationLoading(
                    client,
                    answerSessionId,
                    LoadingState.Success,
                );
            } catch (error) {
                // Best-effort cache update. No-op when session cache is unavailable.
            }

            getAnswerDataQueryAbortSubject(answerSessionId).next();
            dataCancel(cancelIds).catch(() => {
                // Best-effort backend cancel. Local cancellation UX should not be blocked.
            });

            // The fired ids are now cancelled and in-flight vizs have refreshed to
            // new ids via the abort subscription above, so drop the dispatched ids
            // to keep the set scoped to genuinely in-flight queries.
            clearRegisteredCancelIds(answerSessionId);

            return true;
        },
        [
            answerSessionId,
            cancelSearchRunPhase,
            client,
            dataCancel,
            emitQueryCancelled,
            isSearchExecutionInProgress,
            setIsAnswerLoading,
            setSearchExecutionState,
        ],
    );

    const clearSearchExecutionState = React.useCallback(() => {
        setSearchExecutionState(SearchExecutionState.Idle);
    }, [setSearchExecutionState]);

    const startSearchExecution = React.useCallback(() => {
        const state = getExistingAnswerCancellationState(answerSessionId);
        if (state) {
            state.wasCancelled = false;
        }
        // Wipe any leftover ids from a prior execution (e.g. an orphan from a viz
        // that unmounted mid-load) before the new execution's vizs register their
        // fresh trace ids. Guaranteed backstop so the set holds only current ids.
        clearRegisteredCancelIds(answerSessionId);
        // Also bound the emit-dedup set: those prior cancelIds are unregistered by
        // now and can't be re-emitted, so it's safe to forget them — keeps the set
        // ~per-execution instead of growing for every cancel in the session.
        emittedCancelledIdsRef.current.clear();
        setSearchExecutionState(SearchExecutionState.InProgress);
    }, [answerSessionId, setSearchExecutionState]);

    return {
        searchExecutionState,
        isQueryCancelled:
            searchExecutionState === SearchExecutionState.Cancelled,
        isSearchExecutionInProgress,
        cancelSearchExecution,
        clearSearchExecutionState,
        startSearchExecution,
    };
};
